// CLI v0.8 — cloudgrid unplug command.
//
// Takes a node off the grid. Three modes per spec v17 §8:
//   --dark    → stopped (reversible, no confirmation)
//   --hard    → archived immediately, no grace period (confirmation required)
//   default   → archived with 7-day resource grace period (confirmation required)
//
// There is intentionally NO `--archive` flag: the default IS archive.
// Earlier phase-2 kickoff stubs proposed `unplug --archive` for the
// archived transition and `unplug` for `'stopped'`; spec v17 §8 reversed
// that mapping (default = archive with grace; `--dark` = stopped). The
// audit at docs/audits/2026-04-29-pr-162-m4-unplug-audit.md confirms
// v17 is the authoritative shape. If you want to archive, just run
// `cloudgrid unplug` (or `--hard` for the no-grace variant).
//
// See docs/strategy/cli-ux-spec-v17.md §8 and
//     docs/decisions/012-plug-verb-and-charged-state.md.

import { Command } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import { resolveEntityTarget } from '../services/resolve-entity-target.js';
import { assertEntityIsRuntime } from '../lib/assert-entity-kind.js';

export interface UnplugOpts {
  dark?: boolean;
  hard?: boolean;
  skipConfirm?: boolean;
}

interface UnplugResponse {
  entity_id: string;
  status: 'stopped' | 'archived';
  mode: 'dark' | 'default' | 'hard';
  /**
   * Set on `mode: 'hard'` — the server returns 202 with a deploy trace
   * pointing at the async K8s teardown event. Surface it so the operator
   * can poll progress (backlog 040).
   */
  trace_id?: string;
}

export async function runUnplug(
  client: IV2Client,
  name: string | undefined,
  opts: UnplugOpts,
  env: { cwd: string },
): Promise<void> {
  // 1. Determine mode
  const mode: 'dark' | 'hard' | 'default' =
    opts.dark ? 'dark' : opts.hard ? 'hard' : 'default';

  // 2. Resolve target entity. With an explicit `name` argument, the slug is
  // looked up via the v2 API and link.json is NOT required (backlog 099-A —
  // help text says `(uses linked entity if omitted)`, so passing the slug
  // must work outside a linked dir). Without `name`, falls back to
  // `.cloudgrid/link.json` and validates against the active org.
  //
  // Backlog 269 — when the user typed an explicit name we check entity
  // kind BEFORE the runtime-shaped lookup so inspirations don't surface
  // as `NOT_A_RUNTIME` (which Asaf hit 6 times in the audit window).
  // Linked-dir resolution skips this because link.json only points at
  // runtime entities by construction. Redirect copy lives in
  // `inspirationRedirectFor` so all verbs stay in sync.
  if (name !== undefined && name.trim().length > 0) {
    try {
      await assertEntityIsRuntime(client, name, 'unplug');
    } catch (err) {
      // CloudGridError → our redirect — re-throw.
      if (err instanceof CloudGridError) throw err;
      // V2ApiError 404 → entity doesn't exist; let resolveEntityTarget
      // surface the canonical error. Any other thrown error → propagate.
      if (!(err instanceof V2ApiError && err.status === 404)) throw err;
    }
  }
  const target = await resolveEntityTarget(client, name, env.cwd);

  // 3. Display name. Prefer the slug the user typed (or the canonical slug
  // returned by the server lookup) over a UUID for the confirmation prompt
  // and stdout messages.
  const displayName = name ?? target.name;

  // 4. Confirmation prompt (skip for --dark or --skip-confirm)
  if (mode !== 'dark' && !opts.skipConfirm) {
    let message: string;
    if (mode === 'default') {
      message = [
        `This will archive ${displayName}.`,
        `  Outlet will return 410 Gone.`,
        `  Resources torn down in 7 days.`,
        `  Entity row preserved.`,
        `Continue?`,
      ].join('\n');
    } else {
      // hard
      message = [
        `This will immediately archive ${displayName}.`,
        `  Resources torn down NOW. No grace period.`,
        `  Entity row preserved.`,
        `Continue?`,
      ].join('\n');
    }

    const { ok } = await inquirer.prompt([
      {
        type: 'confirm',
        name: 'ok',
        message,
        default: false,
      },
    ]);

    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  // 5. POST to the unplug endpoint using the canonical entity_id (UUID).
  const response = await client.post<UnplugResponse>(
    `/api/v2/entities/${target.entity_id}/unplug`,
    { mode },
  );

  // 6. Print success output per spec v17 §8
  if (mode === 'dark') {
    process.stdout.write(
      `○ ${displayName} powered down.\n` +
        `  Outlet returns 503.\n` +
        `  Run: cloudgrid plug ${displayName}   to bring it back.\n`,
    );
  } else if (mode === 'hard') {
    // Server returns 202 + trace_id; K8s teardown is async (backlog 040).
    let line = `✕ ${displayName} unplugged. Archived. K8s teardown running in the background.\n`;
    if (response?.trace_id) {
      line += `  Trace: ${response.trace_id}\n`;
    }
    process.stdout.write(line);
  } else {
    process.stdout.write(`✕ ${displayName} unplugged.\n`);
  }
}

export const unplugCommand = new Command('unplug')
  .description('Take a node off the grid')
  .argument('[name]', 'Entity name (uses linked entity if omitted)')
  .option('--dark', 'Power down without removing (stopped, fully reversible)')
  .option('--hard', 'Immediate teardown, no grace period')
  .option('--skip-confirm', "Don't prompt for confirmation")
  .action(async (name: string | undefined, opts: UnplugOpts) => {
    try {
      const client = await getV2Client();
      await runUnplug(client, name, opts, { cwd: process.cwd() });
    } catch (err) {
      await handleV2Error(err);
    }
  });
